import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import DischargeSummaryForm from "./DischargeSummaryForm";
import { createDischargeSummary, clearError } from "../../../store/slices/dischargeSummarySlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddDischargeSummary = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId, program, type_id } = state || {}; // ✅ Get type_id from navigation state

    // Redux state
    const { isLoading, error } = useSelector((state) => state.dischargeSummary);
    console.log("Navigation state:", { caseId, patientId, program, type_id });

    const [formData, setFormData] = useState({
        // Foreign Keys
        patient_id: patientId,
        case_id: caseId,
        program_master_id: program?.id || null,
        type_id: type_id || null, // ✅ NEW: Store type_id
        
        // Basic Information
        client_name: "",
        CNIC: "",
        gender: "",
        admission_date: "",
        discharge_date: "",
        program: program?.name,
        discharge_reason: "",

        // Provisional Psychiatric Diagnosis
        primary_diagnosis: "",
        secondary_diagnosis: "",
        presenting_symptoms: "",

        discharge_types: [],
        discharge_extras: [],
        teaching_provided_to: [],

        discharge_type_other_value: "",
        discharge_note: "",
        follow_up_plan: "",
        special_instructions: "",
        injections: "",
        red_flags: "",
        risk_assessment: "",

        // Services Provided
        service_provided: "",

        // Report Sections
        primary_care_attachment: null,
        primary_care_remarks: "",
        psychiatrist_attachment: null,
        psychiatrist_remarks: "",
        care_manager_attachment: null,
        care_manager_remarks: "",

        clinical_progress: [],
        mental_state_at_discharge: [],
        complications: "",

        // Medications
        discharge_medications: [
            { name: "", dosage: "", frequency: "", route: "" }
        ],

        instructions: [],
        family_counseling: [],

        possible_relapse_signs: "",
        recovery_goals: "",
        who_to_contact: "",
        where_to_go_crisis: "",
        medication_details: "",
        treatment_plan: "",
        physical_health_needs: "",
        date_of_review: "",
        admission_pathway: "",

        follow_up_psychiatrist: "",
        referred_to_other_text: "",
        referred_for: [],
        referred_for_other_text: "",
        next_of_kin_informed: "",
        emergency_contact_provided: "",
        safety_care_plan: "",

        // Final Section
        trh_recommendations: "",
    });

    // Clear errors on component mount
    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            client_name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            CNIC: patient.cnic || "",
                            gender: patient.gender || "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient details:", err));
        }
    }, [dispatch, patientId]);

    // Handle form changes from child component
    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        // ----- 1. FILE INPUTS -----
        if (type === "file") {
            setFormData((prev) => ({
                ...prev,
                [name]: files[0] || null,
            }));
            return;
        }

        // ----- 2. MULTI-SELECT CHECKBOX GROUPS (arrays) -----
        const multiSelectFields = [
            "howHeard",
            "discharge_types",
            "discharge_extras",
            "teaching_provided_to",
            "service_provided",
            "clinical_progress",
            "mental_state_at_discharge",
            "instructions",
            "family_counseling",
            "referred_for"
        ];

        if (multiSelectFields.includes(name)) {
            setFormData((prev) => ({
                ...prev,
                [name]: checked
                    ? [...prev[name], value]
                    : prev[name].filter((v) => v !== value),
            }));
            return;
        }

        // ----- 3. BOOLEAN CHECKBOXES (true/false) -----
        const booleanCheckboxes = [
            "referred_to_other",
            "referred_for_daycare",
            "referred_for_home_visiting",
            "referred_for_other",
        ];

        if (booleanCheckboxes.includes(name)) {
            setFormData((prev) => ({
                ...prev,
                [name]: checked,
            }));
            return;
        }

        // ----- 4. NESTED OBJECT FIELDS (e.g., referredBy.xyz) -----
        if (name.startsWith("referredBy.")) {
            const key = name.split(".")[1];
            setFormData((prev) => ({
                ...prev,
                referredBy: {
                    ...prev.referredBy,
                    [key]: value,
                }
            }));
            return;
        }

        // ----- 5. RADIO, TEXT, TEXTAREA, SELECT -----
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle medications array updates from child component
    const handleMedicationsChange = (medications) => {
        setFormData((prev) => ({
            ...prev,
            discharge_medications: medications,
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // ----- 1️⃣ Adjust conditional fields before submission -----
        const adjustedFormData = {
            ...formData,
            discharge_type_other_value: formData.discharge_types.includes("Other")
                ? formData.discharge_type_other_value
                : null,
            complications: formData.mental_state_at_discharge.includes("Behavioral Support Needed Complications")
                ? formData.complications
                : null,
            referred_for_other_text: formData.referred_for.includes("Other")
                ? formData.referred_for_other_text
                : null,
        };

        // ----- 2️⃣ Create FormData object for file uploads -----
        const submitData = new FormData();

        Object.keys(adjustedFormData).forEach(key => {
            if (key === 'discharge_medications') {
                const validMedications = adjustedFormData.discharge_medications.filter(med => med.name?.trim() !== "");
                if (validMedications.length > 0) {
                    validMedications.forEach((med, index) => {
                        submitData.append(`discharge_medications[${index}][name]`, med.name || '');
                        submitData.append(`discharge_medications[${index}][dosage]`, med.dosage || '');
                        submitData.append(`discharge_medications[${index}][frequency]`, med.frequency || '');
                        submitData.append(`discharge_medications[${index}][route]`, med.route || '');
                    });
                } else {
                    submitData.append(`discharge_medications[0][name]`, '');
                    submitData.append(`discharge_medications[0][dosage]`, '');
                    submitData.append(`discharge_medications[0][frequency]`, '');
                    submitData.append(`discharge_medications[0][route]`, '');
                }
            } else if (adjustedFormData[key] !== null && adjustedFormData[key] !== undefined && adjustedFormData[key] !== "") {
                submitData.append(key, adjustedFormData[key]);
            }
        });

        // Debug: Log FormData contents
        console.log("FormData contents:");
        for (let pair of submitData.entries()) {
            console.log(pair[0] + ': ', pair[1]);
        }

        try {
            const response = await dispatch(createDischargeSummary(submitData)).unwrap();

            Swal.fire({
                title: "Success!",
                text: response.message || "Discharge Summary created successfully.", // Backend message here
                icon: "success",
                timer: 3000, // Thoda sa time badha diya taake user replicate wala message parh sake
                showConfirmButton: false,
            });

            navigate(`/cases/${formData.case_id}/profile`, { 
                state: { patientId: formData.patient_id, caseId: formData.case_id } 
            });
        } catch (error) {
            console.error("Submission error:", error);

            let errorMessage = "Failed to create Discharge Summary";
            if (typeof error === 'object' && error !== null) {
                const firstError = Object.values(error)[0]?.[0];
                if (firstError) {
                    errorMessage = firstError;
                }
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            Swal.fire({
                title: "Error!",
                text: errorMessage,
                icon: "error",
                confirmButtonText: "Try Again",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <DischargeSummaryForm
                                formData={formData}
                                handleChange={handleChange}
                                handleMedicationsChange={handleMedicationsChange}
                                isSubmitting={isLoading}
                                program={program}
                                typeId={type_id} // ✅ Pass type_id to form
                            />
                            <div className="row mt-4">
                                <div className="col-md-2 ms-auto">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={isLoading}
                                    >
                                        <i className="ri-save-3-line me-1"></i>
                                        {isLoading ? "Saving..." : "Save"}
                                    </button>
                                </div>
                                <div className="col-md-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100"
                                        onClick={() => navigate(`/cases/${caseId}/profile`, { 
                                            state: { patientId, caseId } 
                                        })}
                                        disabled={isLoading}
                                    >
                                        <i className="ri-arrow-left-line me-1"></i> Cancel
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddDischargeSummary;